/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButton, IconButton } from '@fluentui/react/lib/Button';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import { CollapseAllVisibility, IColumn, IDetailsRowStyleProps, IDetailsRowStyles } from '@fluentui/react/lib/DetailsList';
import { IDropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { FocusZone } from '@fluentui/react/lib/FocusZone';
import { SelectableOptionMenuItemType } from '@fluentui/react/lib/SelectableOption';
import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ITextField, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { Observer, observer } from 'mobx-react';
import React, { useCallback } from 'react';
import { defineMessages } from 'react-intl';
import { CluUtteranceRenderer } from 'src/clu/appShell/cluTagUtterancesPage/components/CluUtteranceRenderer';
import { cluTeachingBubbleMessages } from 'src/clu/cluTeachingBubbleMessages';
import { CluUtteranceSwagger } from 'src/clu/core/services/cluUtteranceSwagger';
import { CluGptPredictedUtteranceStore } from 'src/clu/core/stores/CluGptPredictedUtteranceStore';
import { CluIntentStore } from 'src/clu/core/stores/CluIntentStore';
import { CluUtteranceStore } from 'src/clu/core/stores/CluUtteranceStore';
import { LabelDto } from 'src/clu/modules/hooks/useCluUtteranceActions';
import { cluUtteranceEntityLabelsToLabelDtoS } from 'src/clu/modules/utils/labelingUtils';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { logButtonClick, logTextEntry } from 'src/core/services/telemetry/eventsNamesTemplate';
import { CultureStore } from 'src/core/stores/CultureStore';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { EntityStore } from 'src/core/types/EntityStore';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { LabelingStore } from 'src/luis/projects/utterances/stores/LabelingStore';
import { EditUtteranceModal } from 'src/luis/verticals/entityDetails/components/entityExamples/EditUtteranceModal';
import { DatasetType } from 'src/modules/modelTraining/types/trainingTypes';

export type UtteranceDto = Pick<CluUtteranceStore, 'language' | 'text' | 'labeledIntentName' | 'dataset'>;
export type UtteranceTableProps = TableProps<CluUtteranceStore> & {
    defaultLanguage: string;
    entities: EntityStore[];
    selectedEntityId: string;
    selectedIntentId?: string;
    utteranceToEditId: number;
    intents: CluIntentStore[];
    isLabelingDisabled: boolean;
    currentDataset: DatasetType;
    shouldShowLanguages: boolean;
    labelingStore: LabelingStore;
    onDeleteUtterance: () => void;
    onDismissEditUtterance: () => void;
    onCreateNewIntent: (query: string) => void;
    onEditUtterance: (utteranceId: number) => void;
    languages: Pick<CultureStore, 'code' | 'name'>[];
    acceptGeneratedUtterance: (utteranceId: number) => void;
    rejectGeneratedUtterance: (utteranceId: number) => void;
    submitUtterance: (newUtteranceData: UtteranceDto) => void;
    onRemoveLabels: (utteranceId: number, labels: LabelDto[]) => void;
    onSubmitEditUtterance: (updatedUtteranceData: UtteranceDto) => void;
    addLabel: (utteranceId: number, label: LabelDto, method: 'menu' | 'brush' | 'createNewEntity') => void;
};

const messages = defineMessages({
    intent: { id: 'clu.cluUtteranceTable.intent', defaultMessage: 'Intent' },
    language: { id: 'clu.cluUtteranceTable.language', defaultMessage: 'Language' },
    utterance: { id: 'clu.cluUtteranceTable.utterance', defaultMessage: 'Utterance' },
    testingSetTapHeader: { id: 'clu.cluTagUtterancesPage.testingSetTapHeader', defaultMessage: 'Testing set' },
    trainingSetTapHeader: { id: 'clu.cluTagUtterancesPage.trainingSetTapHeader', defaultMessage: 'Training set' },
    languagePlaceholder: { id: 'clu.cluUtteranceTable.languagePlaceholder', defaultMessage: 'Language' },
    intentPlaceholder: { id: 'clu.cluUtteranceTable.intentPlaceholder', defaultMessage: 'Select intent' },
    dismissEditUtterance: { id: 'clu.cluUtteranceTable.dismissEditUtterance', defaultMessage: 'Dismiss' },
    createNewIntent: { id: 'clu.cluUtteranceTable.createNewIntent', defaultMessage: 'Create new intent' },
    submitEditUtterance: { id: 'clu.cluUtteranceTable.submitEditUtterance', defaultMessage: 'Update utterance' },
    intentsDropDownAriaLabel: { id: 'clu.cluUtteranceTable.intentsDropDownAriaLabel', defaultMessage: 'Intents dropdown' },
    cancelUtteranceEditingAriaLabel: { id: 'clu.cluUtteranceTable.cancelUtteranceEditingAriaLabel', defaultMessage: 'Cancel' },
    languagesDropDownAriaLabel: { id: 'clu.cluUtteranceTable.languagesDropDownAriaLabel', defaultMessage: 'Languages dropdown' },
    submitEditUtteranceDisabled: { id: 'clu.cluUtteranceTable.submitEditUtteranceDisabled', defaultMessage: 'No changes were made' },
    acceptUtteranceEditingButtonAriaLabel: { id: 'clu.cluUtteranceTable.acceptUtteranceEditingButtonAriaLabel', defaultMessage: 'Accept' },
    addUtterancePlaceholder: {
        id: 'clu.cluUtteranceTable.addUtterancePlaceholder',
        defaultMessage: 'Write your example utterance and press enter'
    },
    fieldDisabledMessage: {
        id: 'clu.cluUtteranceTable.fieldDisabledMessage',
        defaultMessage: 'Please select an intent to add the utterance to'
    },
    duplicateUtteranceError: {
        id: 'clu.cluUtteranceTable.duplicateUtteranceError',
        defaultMessage: 'Your utterance "{utteranceText}" is duplicated. No duplicate utterances are allowed.'
    },
    duplicateUtteranceInDifferentDataSetError: {
        id: 'clu.cluUtteranceTable.duplicateUtteranceInDifferentDataSetError',
        defaultMessage: 'Your utterance "{utteranceText}" is duplicated in {dataset}. Duplicate utterances are not recommended.'
    }
});

const { getClassName } = getStylist('CluUtteranceTable');
const scopedTheme = getScopedTheme('CluUtteranceTable');

const expandButtonStyles = getClassName('expandButtonStyles', { display: 'none' });
const addNewUtteranceClassName = getClassName('addNewUtteranceClassName', theme => ({
    borderStyle: 'solid',
    borderWidth: '0px 0px 1px 0px',
    borderColor: theme.borderColor
}));
const editUtteranceInputStyles = (): Partial<ITextFieldStyles> => ({
    ...{ fieldGroup: { backgroundColor: scopedTheme.editInputBackgroundColor } }
});

const gptRowStyles = classNamesFunction<IDetailsRowStyleProps, IDetailsRowStyles>()({
    root: { border: `1px dashed ${scopedTheme.gptColor}` }
});
const cellStyles = classNamesFunction<IDetailsRowStyleProps, IDetailsRowStyles>()({
    cell: { display: 'inline-flex', alignItems: 'center' }
});
const gptInBetweenRowStyles = classNamesFunction<IDetailsRowStyleProps, IDetailsRowStyles>()({
    root: { borderTop: 'none', borderBottom: 'none' }
});

/**
 * Displays the app's list of utterances along with the associated operations and details.
 */
export const CluUtteranceTable = observer((props: UtteranceTableProps) => {
    const {
        items,
        intents,
        sortKey,
        entities,
        addLabel,
        languages,
        onSortColumn,
        labelingStore,
        onRemoveLabels,
        currentDataset,
        defaultLanguage,
        onEditUtterance,
        submitUtterance,
        selectedEntityId,
        utteranceToEditId,
        onDeleteUtterance,
        onCreateNewIntent,
        isLabelingDisabled,
        shouldShowLanguages,
        selectedIntentId = '',
        onSubmitEditUtterance,
        onDismissEditUtterance,
        acceptGeneratedUtterance,
        rejectGeneratedUtterance
    } = props;
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { rootStore, uiStore, dispatcher } = useGlobalState();

    const { currentCluProjectStore: currentProject } = rootStore;
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const doNotShowEditUtteranceModal = uiStore.isEditUtteranceDisclaimerModalHidden;

    const utteranceTextLimit = 500;
    const defaultUtteranceData: UtteranceDto = {
        text: '',
        dataset: currentDataset,
        language: defaultLanguage,
        labeledIntentName: selectedIntentId
    };

    const intentsDropDownIdRef = React.useRef<string>(getId());
    const newUtteranceFieldIdRef = React.useRef<string>(getId());

    const [isEditUtteranceModalHidden, setIsEditUtteranceModalHidden] = React.useState(true);
    const [newUtteranceData, setNewUtteranceData] = useUpdateState<UtteranceDto>(defaultUtteranceData);
    const [editUtteranceData, setEditUtteranceData] = useUpdateState<UtteranceDto>(defaultUtteranceData);
    const [isEditUtteranceSubmitButtonDisabled, setIsEditUtteranceSubmitButtonDisabled] = React.useState<boolean>(true);

    const utteranceInputRef = React.useRef<ITextField>(null);
    const intentDropdownRef = React.useRef<IDropdown>(null);
    const editBtnRef = React.useRef<IButton>(null);
    const cancelBtnRef = React.useRef<IButton>(null);

    React.useEffect(() => {
        const firstRow = document.querySelectorAll('[role="row"]')[0] as HTMLElement;
        const focusableCells = firstRow?.querySelectorAll('[data-is-focusable="true"]');
        focusableCells?.forEach(element =>
            (element as HTMLElement).addEventListener('keydown', e => {
                if (e.shiftKey) {
                    return;
                }

                if (e.key === LuisKeyCodes.Tab) {
                    e.stopPropagation();
                    e.preventDefault();
                    document.getElementById(intentsDropDownIdRef.current)?.focus();
                }
            })
        );
    });

    React.useEffect(
        () => () => {
            dequeueTeachingBubble('CluSelectIntent');
        },
        []
    );

    React.useEffect(() => {
        if (canWrite && !uiStore.teachingBubbles.CluSelectIntent) {
            createTeachingBubble(
                'CluSelectIntent',
                localize(cluTeachingBubbleMessages.selectIntentBody),
                localize(cluTeachingBubbleMessages.selectIntentTitle),
                `#${intentsDropDownIdRef.current}`,
                true,
                true
            );
        }
    }, [canWrite, Object.values(uiStore.teachingBubbles)]);

    const sortColumnObject = (field: keyof CluUtteranceStore) => ({
        isSorted: sortKey?.key === field,
        isSortedDescending: sortKey?.key === field && sortKey?.order === 'desc'
    });

    const handleTableHeaderKeyDown = (
        e: React.KeyboardEvent<HTMLDivElement | HTMLInputElement | HTMLTextAreaElement>,
        shouldStopPropagation: boolean
    ) => {
        if (shouldStopPropagation) {
            e.stopPropagation();
        }

        if (e.shiftKey) {
            return;
        }

        if (e.key === LuisKeyCodes.Tab) {
            e.preventDefault();
            const tableRows = document.querySelectorAll('[role="row"]');
            for (let index = 1; index < tableRows.length; index++) {
                const row = tableRows[index] as HTMLElement;
                const isSelected = row.getAttribute('aria-selected');
                if (isSelected === 'true') {
                    row?.focus();
                    return;
                }
            }

            (tableRows[1] as HTMLElement)?.focus();
        }
    };

    const onUtteranceTextEnterKeyUp = () => {
        if (!newUtteranceData.text) {
            return;
        }

        if (newUtteranceData.labeledIntentName && newUtteranceData.language && !duplicatedUtteranceErrorMessage) {
            submitUtterance({ ...newUtteranceData, labeledIntentName: newUtteranceData.labeledIntentName });
            setNewUtteranceData({ text: '' });
            logTextEntry(serviceName, 'DataLabeling', 'UtteranceEntry', currentProject.id);
        }
    };

    const onIntentDropdownChange = (intentOption: IDropdownOption) => {
        if (intentOption.key === 'createNewIntent') {
            logButtonClick(serviceName, 'DataLabeling', 'AddIntent', newUtteranceData.dataset, currentProject.id);
            onCreateNewIntent((intentOption.data as { query?: string })?.query);
        } else {
            setNewUtteranceData({ labeledIntentName: intentOption.key as string });
        }
    };

    const intentDropdownOptions: IDropdownOption[] = [
        ...intents.map(i => ({ key: i.name, text: i.name, selected: newUtteranceData.labeledIntentName === i.name }))
    ];
    const onRenderTailActionItems = (query: string): IDropdownOption[] => [
        { key: 'splitter', text: '', itemType: SelectableOptionMenuItemType.Divider },
        { key: 'createNewIntent', disabled: !canWrite, text: localize(messages.createNewIntent), data: { query, isActionItem: true } }
    ];

    const getDuplicatedErrorMessage = useCallback(
        ({ utterances, newUtterance }: { utterances: CluUtteranceSwagger[]; newUtterance: UtteranceDto }) => {
            const duplicatedUtterance = utterances.find(e => e.text === newUtterance.text);
            let duplicatedUtteranceErrorMessage: string | undefined;
            let duplicatedUtteranceInDifferentDataSetErrorMessage: string | undefined;

            if (duplicatedUtterance) {
                if (duplicatedUtterance.dataset === currentDataset) {
                    duplicatedUtteranceErrorMessage = localize(messages.duplicateUtteranceError, { utteranceText: newUtterance.text });
                } else {
                    duplicatedUtteranceInDifferentDataSetErrorMessage = localize(messages.duplicateUtteranceInDifferentDataSetError, {
                        utteranceText: newUtterance.text,
                        dataset:
                            currentDataset === 'Test' ? localize(messages.trainingSetTapHeader) : localize(messages.testingSetTapHeader)
                    });
                }
            }

            return { duplicatedUtteranceErrorMessage, duplicatedUtteranceInDifferentDataSetErrorMessage };
        },
        [currentDataset]
    );

    const { duplicatedUtteranceErrorMessage, duplicatedUtteranceInDifferentDataSetErrorMessage } = newUtteranceData.text
        ? getDuplicatedErrorMessage({
              utterances: currentProject.jsonData.utterances,
              newUtterance: newUtteranceData
          })
        : { duplicatedUtteranceErrorMessage: undefined, duplicatedUtteranceInDifferentDataSetErrorMessage: undefined };

    const editUtteranceText = items.find(item => item.id === utteranceToEditId)?.text;
    const {
        duplicatedUtteranceErrorMessage: duplicatedEditUtteranceErrorMessage,
        duplicatedUtteranceInDifferentDataSetErrorMessage: duplicatedEditUtteranceInDifferentDataSetErrorMessage
    } = utteranceToEditId
        ? getDuplicatedErrorMessage({
              utterances: currentProject.jsonData.utterances.filter(utterance => utterance.text !== editUtteranceText),
              newUtterance: editUtteranceData
          })
        : { duplicatedUtteranceErrorMessage: undefined, duplicatedUtteranceInDifferentDataSetErrorMessage: undefined };

    const getNewUtteranceTextField = (disabled: boolean) => (
        <LuisTextField
            ariaLabel={localize(messages.utterance)}
            autoFocus
            disabled={disabled}
            value={newUtteranceData.text}
            id={newUtteranceFieldIdRef.current}
            onEnterKeyUp={onUtteranceTextEnterKeyUp}
            errorMessage={duplicatedUtteranceErrorMessage || duplicatedUtteranceInDifferentDataSetErrorMessage}
            placeholder={localize(messages.addUtterancePlaceholder)}
            data-automation-id="newUtteranceRow"
            onRenderDescription={() => (
                <Stack horizontalAlign="end">
                    <LuisText variant="smallPlus" color={scopedTheme.secondaryTextColor}>
                        {newUtteranceData.text.length}/{utteranceTextLimit}
                    </LuisText>
                </Stack>
            )}
            onChange={(_, value) => setNewUtteranceData({ text: value.slice(0, utteranceTextLimit) })}
            onKeyDown={e =>
                handleTableHeaderKeyDown(
                    e,
                    e.key === LuisKeyCodes.ArrowRight ||
                        (document.getElementById(newUtteranceFieldIdRef.current) as HTMLInputElement)?.selectionStart > 0
                )
            }
        />
    );

    const onRenderAddNewUtteranceRow = () => (
        <FocusZone>
            <Stack horizontal tokens={{ childrenGap: 8, padding: '33px 0px 20px 0px' }} styles={{ root: addNewUtteranceClassName }}>
                <LuisDropdown
                    isLoading={false}
                    options={intentDropdownOptions}
                    styles={{ root: { width: 200 } }}
                    id={intentsDropDownIdRef.current}
                    displayedUserRole={displayedUserRole}
                    selectedKey={newUtteranceData.labeledIntentName}
                    onRenderTailActionItems={onRenderTailActionItems}
                    placeholder={localize(messages.intentPlaceholder)}
                    onKeyDown={e => handleTableHeaderKeyDown(e, false)}
                    onChange={(_, item) => onIntentDropdownChange(item)}
                    ariaLabel={localize(messages.intentsDropDownAriaLabel)}
                    onClick={() => dequeueTeachingBubble('CluSelectIntent')}
                />

                {shouldShowLanguages && (
                    <LuisDropdown
                        isLoading={false}
                        styles={{ root: { width: 135 } }}
                        selectedKey={newUtteranceData.language}
                        onKeyDown={e => handleTableHeaderKeyDown(e, false)}
                        placeholder={localize(messages.languagePlaceholder)}
                        ariaLabel={localize(messages.languagesDropDownAriaLabel)}
                        onChange={(_, item) => setNewUtteranceData({ language: item.key as string })}
                        options={languages.map(l => ({ key: l.code, text: l.name, selected: newUtteranceData.language === l.code }))}
                    />
                )}
                <StackItem grow>
                    {canWrite ? (
                        <LuisTooltip
                            directionalHint={DirectionalHint.topLeftEdge}
                            content={!newUtteranceData.labeledIntentName && localize(messages.fieldDisabledMessage)}>
                            {getNewUtteranceTextField(!newUtteranceData.labeledIntentName)}
                        </LuisTooltip>
                    ) : (
                        <RBACTooltip
                            hidden={canWrite}
                            displayedUserRole={displayedUserRole}
                            data-automation-id="addNewUtteranceLanguageToolTip"
                            componentSurroundedByToolTip={getNewUtteranceTextField(!canWrite)}
                        />
                    )}
                </StackItem>
            </Stack>
        </FocusZone>
    );

    const onRenderIntentColumn = (item: CluUtteranceStore) =>
        item.id === utteranceToEditId ? (
            <Observer>
                {() => (
                    <LuisDropdown
                        isLoading={false}
                        key={item.labeledIntentName}
                        componentRef={intentDropdownRef}
                        styles={{ root: { width: 135 } }}
                        selectedKey={editUtteranceData.labeledIntentName}
                        placeholder={localize(messages.intentPlaceholder)}
                        ariaLabel={localize(messages.intentsDropDownAriaLabel)}
                        options={intents.map(i => ({
                            key: i.name,
                            text: i.name,
                            selected: item.labeledIntentName === editUtteranceData.labeledIntentName
                        }))}
                        onChange={(_, option) => setEditUtteranceData({ labeledIntentName: option.key as string })}
                    />
                )}
            </Observer>
        ) : (
            <TruncatedText title={(item as CluGptPredictedUtteranceStore).updatedLabeledIntentName ?? item.labeledIntentName}>
                {(item as CluGptPredictedUtteranceStore).updatedLabeledIntentName ?? item.labeledIntentName}
            </TruncatedText>
        );

    const onRenderLanguageColumn = (item: CluUtteranceStore) =>
        item.id === utteranceToEditId ? (
            <Observer>
                {() => (
                    <LuisDropdown
                        key={item.language}
                        isLoading={false}
                        styles={{ root: { width: 135 } }}
                        selectedKey={editUtteranceData.language}
                        placeholder={localize(messages.languagePlaceholder)}
                        ariaLabel={localize(messages.languagesDropDownAriaLabel)}
                        onChange={(_, option) => setEditUtteranceData({ language: option.key as string })}
                        options={languages.map(l => ({ key: l.code, text: l.name, selected: editUtteranceData.language === l.code }))}
                    />
                )}
            </Observer>
        ) : (
            <LuisText>{languages.find(l => l.code === (item.language ?? defaultLanguage))?.name}</LuisText>
        );

    const editUtterance = () => {
        onRemoveLabels(utteranceToEditId, cluUtteranceEntityLabelsToLabelDtoS(items.find(i => i.id === utteranceToEditId).labeledEntities));
        onSubmitEditUtterance(editUtteranceData);
    };
    const handleEditUtteranceSubmit = () => {
        if (duplicatedEditUtteranceErrorMessage) {
            return;
        }
        if (doNotShowEditUtteranceModal || !items.find(i => i.id === utteranceToEditId).labeledEntities.length) {
            editUtterance();
        } else {
            setIsEditUtteranceModalHidden(false);
        }
    };

    const handleEditUtteranceModalSubmit = (doNotShowAgain: boolean) => {
        setIsEditUtteranceModalHidden(true);
        editUtterance();
        if (doNotShowAgain) {
            dispatcher.dispatch('hideEditUtteranceDisclaimerModal');
        }
    };

    const handleUtteranceEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();

        switch (e.key) {
            case LuisKeyCodes.Escape:
                onDismissEditUtterance();
                break;
            case LuisKeyCodes.Enter:
                if (isEditUtteranceSubmitButtonDisabled) {
                    onDismissEditUtterance();
                } else {
                    handleEditUtteranceSubmit();
                    e.preventDefault();
                }
                break;
            case LuisKeyCodes.ArrowLeft:
                if (utteranceInputRef?.current?.selectionStart === 0 && utteranceInputRef?.current?.selectionEnd === 0) {
                    intentDropdownRef?.current?.focus();
                }
                break;
            case LuisKeyCodes.ArrowRight:
                if (
                    utteranceInputRef?.current?.selectionStart === editUtteranceData.text.length &&
                    utteranceInputRef?.current?.selectionEnd === editUtteranceData.text.length
                ) {
                    if (isEditUtteranceSubmitButtonDisabled) {
                        cancelBtnRef?.current?.focus();
                    } else {
                        editBtnRef?.current?.focus();
                    }
                }
                break;
            default:
        }
    };

    const onRenderUtteranceColumn = (item: CluUtteranceStore) =>
        item.id === utteranceToEditId ? (
            <Stack grow horizontal verticalAlign="center" horizontalAlign="space-between">
                <Stack.Item grow>
                    {(duplicatedEditUtteranceErrorMessage || duplicatedEditUtteranceInDifferentDataSetErrorMessage) && (
                        <Stack style={{ height: '22px' }} />
                    )}

                    <LuisTextField
                        ariaLabel={localize(messages.submitEditUtterance)}
                        componentRef={utteranceInputRef}
                        styles={editUtteranceInputStyles}
                        value={editUtteranceData.text}
                        onChange={(_, text) => setEditUtteranceData({ text })}
                        onKeyDown={handleUtteranceEditKeyDown}
                        errorMessage={duplicatedEditUtteranceErrorMessage || duplicatedEditUtteranceInDifferentDataSetErrorMessage}
                    />
                </Stack.Item>
                <Stack horizontal verticalAlign="center">
                    <LuisTooltip
                        content={localize(
                            isEditUtteranceSubmitButtonDisabled ? messages.submitEditUtteranceDisabled : messages.submitEditUtterance
                        )}>
                        <IconButton
                            componentRef={editBtnRef}
                            iconProps={{ iconName: 'Accept' }}
                            onClick={handleEditUtteranceSubmit}
                            disabled={isEditUtteranceSubmitButtonDisabled}
                            ariaLabel={localize(messages.acceptUtteranceEditingButtonAriaLabel)}
                        />
                    </LuisTooltip>
                    <LuisTooltip content={localize(messages.dismissEditUtterance)}>
                        <IconButton
                            componentRef={cancelBtnRef}
                            iconProps={{ iconName: 'Cancel' }}
                            onClick={onDismissEditUtterance}
                            ariaLabel={localize(messages.cancelUtteranceEditingAriaLabel)}
                        />
                    </LuisTooltip>
                </Stack>
            </Stack>
        ) : (
            <CluUtteranceRenderer
                utterance={item.language ? item : ({ ...item, language: defaultLanguage } as CluUtteranceStore)}
                entities={entities}
                addLabel={addLabel}
                canWrite={canWrite}
                labelingStore={labelingStore}
                onEditUtterance={onEditUtterance}
                selectedEntityId={selectedEntityId}
                displayedUserRole={displayedUserRole}
                onDeleteUtterance={onDeleteUtterance}
                isLabelingDisabled={isLabelingDisabled}
                sidePaneSelectedEntity={selectedEntityId}
                rejectGeneratedUtterance={rejectGeneratedUtterance}
                acceptGeneratedUtterance={acceptGeneratedUtterance}
            />
        );

    const columns: IColumn[] = [
        {
            minWidth: 135,
            maxWidth: 135,
            isResizable: false,
            isCollapsible: false,
            key: 'labeledIntentName',
            fieldName: 'labeledIntentName',
            name: localize(messages.intent),
            ...sortColumnObject('labeledIntentName'),
            onRender: onRenderIntentColumn
        },
        ...(shouldShowLanguages
            ? [
                  {
                      minWidth: 120,
                      maxWidth: 120,
                      key: 'language',
                      isResizable: false,
                      isCollapsible: false,
                      fieldName: 'language',
                      ...sortColumnObject('language'),
                      name: localize(messages.language),
                      onRender: onRenderLanguageColumn
                  }
              ]
            : []),
        {
            key: 'text',
            name: localize(messages.utterance),
            minWidth: 350,
            maxWidth: 700,
            isCollapsible: false,
            isResizable: false,
            onRender: onRenderUtteranceColumn
        }
    ];

    React.useEffect(
        () => setEditUtteranceData(utteranceToEditId ? { ...items.find(i => i.id === utteranceToEditId) } : defaultUtteranceData),
        [utteranceToEditId]
    );

    React.useEffect(() => setNewUtteranceData({ labeledIntentName: selectedIntentId }), [selectedIntentId]);

    React.useEffect(() => {
        const utterance = items.find(i => i.id === utteranceToEditId);
        setIsEditUtteranceSubmitButtonDisabled(
            editUtteranceData.text === utterance?.text &&
                editUtteranceData.labeledIntentName === utterance?.labeledIntentName &&
                editUtteranceData.language === utterance?.language
        );
    }, [editUtteranceData]);

    return (
        <>
            <Table<CluUtteranceStore>
                columns={columns}
                onSortColumn={onSortColumn}
                getKey={(item: CluUtteranceStore) => `${item.id}`}
                focusZoneProps={{ direction: 3, isCircularNavigation: true }}
                onRenderRow={(rowProps, rowDefaultRenderer) =>
                    rowDefaultRenderer({
                        ...rowProps,
                        styles: mergeStyleSets(
                            rowProps.styles,
                            cellStyles,
                            rowProps.item.dataset === 'Generated' && gptRowStyles,
                            rowProps.item.dataset === 'Generated' &&
                                rowProps.itemIndex !== 0 &&
                                rowProps.itemIndex !== items.length - 1 &&
                                gptInBetweenRowStyles
                        )
                    })
                }
                groups={[
                    {
                        startIndex: 0,
                        isShowingAll: true,
                        name: 'Add utterance',
                        key: 'AddUtteranceHeader',
                        count: items.length ? items.length : 1
                    }
                ]}
                groupProps={{
                    onRenderHeader: onRenderAddNewUtteranceRow,
                    collapseAllVisibility: CollapseAllVisibility.hidden,
                    headerProps: { expandButtonProps: { className: expandButtonStyles } }
                }}
                {...props}
            />

            <EditUtteranceModal
                hidden={isEditUtteranceModalHidden}
                onSubmit={handleEditUtteranceModalSubmit}
                onDismiss={() => setIsEditUtteranceModalHidden(true)}
            />
        </>
    );
});
